<template>
  <div
    :style="cssProps"
    :class="{ wrapper: !isMobile, 'wrapper-mobile': isMobile }"
    class="d-flex justify-space-between align-center h-full"
  >
    <div
      v-if="!isMobile"
      class="markero-logo col-6 pa-0 px-10"
    >
      <r-img
        alt="Company Registration Logo"
        :src="markeroRegistrationImg"
        cover
      />
    </div>

    <div class="h-full col-md-6 flex-column d-flex content-wrapper pa-3">
      <div>
        <r-img
          alt="Company Logo"
          class="markero-app-logo"
          :src="logo"
          width="140"
          cover
        />
      </div>

      <div class="flex-1 register px-2 px-sm-0 mx-auto col-sm-10 col-lg-6 inner-container">
        <div class="row justify-center text-center">
          <div class="content">
            <div class="text-center pb-8">
              <h2 class="register-title">
                {{ $t('register.title') }}
              </h2>
            </div>
            <div>
              <v-form
                ref="signupForm"
                name="register"
                @submit.prevent="signup"
              >
                <v-text-field
                  v-model.trim="email"
                  :label="$t('signup.labels.email')"
                  type="email"
                  variant="outlined"
                  hide-details="auto"
                  class="email pb-5"
                  prepend-inner-icon="mdi-email-outline"
                  :rules="[rules.email, rules.required]"
                  required
                  color="primary"
                  validate-on="blur"
                  @click="onEmailClick"
                />

                <v-text-field
                  v-model="password"
                  :label="$t('signup.labels.password')"
                  :type="showPassword ? 'text' : 'password'"
                  hide-details="auto"
                  variant="outlined"
                  color="primary"
                  prepend-inner-icon="mdi-lock-outline"
                  :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  class="password pb-8"
                  :rules="[rules.required]"
                  required
                  validate-on="blur"
                  @click="onPasswordClick"
                  @click:append-inner="showPassword = !showPassword"
                />
                <div class="d-flex justify-space-between pb-8">
                  <v-checkbox
                    v-model="checkbox"
                    color="primary"
                    density="compact"
                    :rules="[rules.required]"
                  >
                    <template v-slot:label>
                      <span class="agree-on-terms ml-2">
                        <Terms />
                      </span>
                    </template>
                  </v-checkbox>
                </div>
                <div class="px-0 d-flex flex-column">
                  <v-expand-transition>
                    <v-alert
                      v-if="error"
                      type="error"
                      density="compact"
                      tile
                      variant="tonal"
                      rounded="0"
                      class="my-4"
                    >
                      <span v-html="$t(getErrorMessage(error))" />
                    </v-alert>
                  </v-expand-transition>

                  <LoadingButton
                    :color="$vuetify.theme.global?.current?.colors['markero-blue']"
                    class="px-10 register-btn"
                    :loading="submitting"
                    type="submit"
                  >
                    {{ $t('buttons.signup.register-now') }}
                  </LoadingButton>
                </div>
              </v-form>
            </div>

            <div class="mt-8 d-flex justify-center">
              <h4 class="account-exists">
                {{ $t('signup.account-exists') }}
              </h4>
              <a
                href="/auth/login"
                :style="infoColor"
                class="ml-2 account-exists login-link"
                @click="onLoginClick"
                >{{ $t('login.form.submit') }}</a
              >
            </div>
          </div>
        </div>
      </div>
      <div class="flex-1 terms-wrapper mb-2 mx-auto">
        <div
          :class="{ terms: !isMobile, 'terms-mobile ma-2': isMobile }"
          class="justify-center d-flex flex-1 flex-wrap"
        >
          <IubendaDocumentLink
            type="terms"
            class="mx-1 mx-sm-2"
            @click.stop
          />
          <IubendaDocumentLink
            class="mx-1 mx-sm-2"
            type="privacyPolicy"
            @click.stop
          /><br />
          <IubendaConsentLink class="mx-1 mx-sm-2" />
          <a
            :href="imprintUrl"
            target="_blank"
            class="mx-1 mx-sm-2"
            rel="noopener noreferrer"
            @click="onImprintUrlClick"
            >{{ $t('navigation.labels.imprint') }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import logo from '@/assets/img/logo.png'
import markeroRegistrationImg from '@/assets/img/markero-registration.png'
import LoadingButton from '@/components/loading-button.vue'
import { isValidPassword } from '@/lib/password'
import { email, required } from '@/lib/validation'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import REGISTER_USER from '@/modules/auth/Register/queries/registerUser.gql'
import Terms from '@/modules/auth/Register/Terms.vue'
import IubendaConsentLink from '@/modules/iubenda/ConsentPreferencesLink.vue'
import IubendaDocumentLink from '@/modules/iubenda/DocumentLink.vue'

export default {
  components: {
    IubendaConsentLink,
    IubendaDocumentLink,
    Terms,
    LoadingButton,
  },
  mixins: [featureMixin, brandingMixin],
  data() {
    return {
      logo,
      submitting: false,
      email: '',
      password: '',
      error: null,
      showPassword: false,
      checkbox: false,
      markeroRegistrationImg,
    }
  },
  computed: {
    cssProps() {
      return {
        '--mid-yellow': this.$vuetify.theme.global?.current?.colors['markero-mid-yellow'],
        '--dark-blue': this.$vuetify.theme.global?.current?.colors['markero-dark-blue'],
        '--dark-grey': this.$vuetify.theme.global?.current?.colors['markero-dark-grey'],
        '--light-orange': this.$vuetify.theme.global?.current?.colors['markero-light-orange'],
        '--primary': this.$vuetify.theme.global?.current?.colors['primary'],
      }
    },
    isMobile() {
      return this.$vuetify.display.mdAndDown
    },
    infoColor() {
      return {
        color: this.$vuetify.theme.global?.current?.colors['markero-blue'],
      }
    },
    title() {
      return this.$route.params.token
        ? this.$t('signup.title-free')
        : this.$t('signup.title-free-trial')
    },
    formReady() {
      return this.email.length && this.password.length
    },
    errorMessages() {
      return {
        emptyFields: 'alerts.signup.empty-fields',
        invalidToken: 'alerts.signup.invalid-token',
        invalidAgreement: 'alerts.signup.invalid-agreement',
        invalidPassword: 'alerts.signup.invalid-password',
        userExists: 'alerts.signup.user-exists',
        unknown: 'alerts.signup.unknown',
      }
    },
  },
  watch: {
    checkbox(val) {
      if (val) {
        this.$tracking.event('Account Creation', 'Clicked', 'AGB DSB Read and AGB Accepted')
      }
    },
  },
  created() {
    this.rules = { email, required }
    const {
      query: { readableIds, productType, isYearly, currencySymbol, countryCode },
    } = this.$route
    sessionStorage.setItem(
      'productPackageQuery',
      JSON.stringify({
        readableIds,
        productType,
        // If isYearly is added programmatically it will be a boolean if it's
        // processed by the browser it's becoming a string (On page refresh)
        // On page refresh, the browser doesn't know the intended data type of the query parameter (It's always string)
        isYearly: isYearly === true || isYearly === 'true',
        currencySymbol,
        countryCode,
      })
    )
  },
  apollo: {
    $client: 'public',
  },
  methods: {
    onPasswordClick() {
      this.$tracking.event('Account Creation', 'Clicked', 'Password')
    },
    onEmailClick() {
      this.$tracking.event('Account Creation', 'Clicked', 'E-Mail')
    },
    onLoginClick() {
      this.$tracking.event('Account Creation', 'Clicked', 'Login')
    },
    onImprintUrlClick() {
      this.$tracking.event('Account Creation', 'Clicked', 'Imprint RegFooter')
    },
    async signup(event) {
      event.preventDefault()
      this.error = null
      const { valid } = await this.$refs.signupForm.validate()
      if (valid && this.formReady && this.checkbox) {
        if (!isValidPassword(this.password)) {
          this.error = 'invalidPassword'
          return
        }
        this.$tracking.event('Account Creation', 'Clicked', 'Register')
        this.submitting = true

        try {
          const hasTrackingConsentGiven = this.$tracking.isMeasurementConsentGiven()
          const utmParams = hasTrackingConsentGiven && this.$utm.getUtmParams()

          const input = {
            email: this.email,
            password: this.password,
            locale: this.$i18n.locale,
            hasTrackingConsentGiven,
            ...(utmParams && { utmParams }),
          }

          if (this.$route.query?.companyId) {
            input.companyId = this.$route.query.companyId
          }

          const { data } = await this.$apollo.mutate({
            mutation: REGISTER_USER,
            context: { withoutAuth: true },
            variables: {
              input: input,
            },
          })

          if (data) {
            sessionStorage.removeItem('anamnesis')

            // this event should be fired if the button "buttons.product-packages.basic-package.label" was pressed
            // TODO: rename event (and adapt pixel event in friendly tag manager)
            this.$tracking.event('StartTrial', 'Clicked')

            await this.$auth.login(this.email, this.password)
          }
        } catch (error) {
          this.error = 'unknown'
        }

        this.submitting = false
      }
    },
    getErrorMessage(code) {
      return this.errorMessages[code] || this.errorMessages.unknown
    },
  },
}
</script>

<style scoped>
/* Wrapper Styles */
.wrapper,
.wrapper-mobile {
  background-color: white;
}

.wrapper {
  width: 100vw;
}

.wrapper > * {
  width: 50%;
}

.wrapper,
.markero-logo {
  height: 100vh;
}

.wrapper-mobile {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Markero Logo Styles */
.markero-logo {
  background-color: rgb(var(--v-theme-markero-mid-yellow));
  display: flex;
  justify-content: center;
  align-items: flex-end;
}

/* Register Styles */
.register-title,
.email,
.password,
.account-exists,
.terms,
.agree-on-terms,
.register-btn {
  color: #102a21;
  font-family: 'Poppins', sans-serif;
}

.register-title {
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
}

.account-exists,
.terms {
  font-weight: 400;
  line-height: normal;
}

.account-exists {
  font-size: 16px;
}

.login-link {
  text-decoration: none;
  font-weight: 700;
}

.login-link:hover {
  color: var(--dark-blue) !important;
}

.inner-container,
.terms-wrapper {
  max-width: 500px;
}

.terms,
.terms-mobile {
  font-size: 14px;
}

.terms-mobile {
  margin-top: 80%;
}

.terms-error .terms-mobile {
  margin-top: 70%;
}

.register-btn {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  color: white;
  height: fit-content !important;
  padding: 14px 34px !important;
  border-radius: 10px;
  box-shadow: 5px 5px 15px 0 rgba(47, 22, 232, 0.25);
  text-transform: none;
}

.content {
  width: 100%;
  max-width: 450px;
}

.agree-on-terms {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: left;
  margin-top: 0;
}

/* Vuetify Specific Overrides */

:deep(.v-selection-control__input > .v-icon) {
  color: var(--light-orange) !important;
}
:deep(.v-text-field .v-field) {
  border-radius: 10px !important;
}

:deep(.v-field--variant-outlined .v-field__outline:not(.v-field--error .v-field__outline)) {
  color: rgba(0, 0, 0, 0.38);
}

:deep(.mdi-email-outline),
:deep(.mdi-lock-outline),
:deep(.mdi-eye),
:deep(.mdi-eye-off) {
  color: var(--primary) !important;
  opacity: 1;
}

:deep(.v-input--selection-controls__input) {
  margin-top: -10px !important;
}
:deep(.theme--light.v-icon) {
  color: var(--light-orange) !important;
}

:deep(.v-input--selection-controls) {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

:deep(.v-application--is-ltr .v-input--selection-controls__input) {
  margin-right: 0 !important;
}

:deep(fieldset) {
  background-color: white;
}

:deep(.theme--light.v-icon) {
  z-index: 99;
  color: rgba(0, 0, 0, 0.54);
}

:deep(
  .theme--light.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state)
    > .v-input__control
    > .v-input__slot
    fieldset
) {
  color: #e4e4e4;
}

:deep(.theme--light.v-input input) {
  color: var(--dark-grey);
}

:deep(.v-text-field--enclosed .v-input__prepend-inner) {
  margin: 12px 0;
}

:deep(.v-text-field--outlined .v-label) {
  top: 15px;
}

:deep(.v-text-field--enclosed .v-input__append-inner) {
  margin-top: 14px !important;
}

:deep(.v-text-field--outlined > .v-input__control > .v-input__slot) {
  min-height: 30px;
}

:deep(.theme--light.v-btn:hover::before) {
  background-color: var(--dark-blue);
}

.text-error {
  padding-bottom: 0 !important;
}

:deep(.v-text-field.v-text-field--enclosed .v-text-field__details) {
  margin-bottom: 2px !important;
  padding-bottom: 2px !important;
}

:deep(
  .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded)
    > .v-input__control
    > .v-input__slot
) {
  margin-bottom: 0 !important;
}

:deep(.password.v-text-field.v-text-field--enclosed .v-text-field__details) {
  margin-bottom: 15px !important;
}

:deep(.v-label--clickable) {
  opacity: 1;
}

@media only screen and (max-width: 450px) {
  .register {
    margin-top: 0;
  }
}

.markero-app-logo {
  margin: 0 auto;
}

@media (max-width: 960px) {
  .content-wrapper {
    gap: 1rem;
  }
}

@media (max-width: 450px) {
  .content-wrapper {
    gap: 3rem;
  }
}

@media (min-width: 960px) {
  .content-wrapper {
    display: flex;
    flex-direction: column !important;
    min-height: 100vh;
    justify-content: space-between;
  }
}
</style>
